import { getBackendErrors, getErrorMessage } from "api/errors";
import { useMember } from "api/members";
import { useTiers } from "api/tiers";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { useStyles } from "./styles";

interface Props {
  closeModal: () => void;
  memberId: string;
  isOpen: boolean;
}

export const AssignToTier = ({ closeModal, memberId, isOpen }: Props) => {
  const classes = useStyles();
  const { assignMemberToTier } = useMember();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { useListOfTierSets } = useTiers();
  const { data: tierSetList } = useListOfTierSets();

  return (
    <OLModal
      header={message({ id: "assignToTier" })}
      onClose={() => closeModal()}
      open={isOpen}
      allowOverflow
      modalWidth={536}
    >
      <Formik
        initialValues={{ levelId: undefined, tierSetId: undefined }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const response = await assignMemberToTier(
              memberId,
              values?.levelId!,
            );
            if (response) {
              closeModal();
              notify(message({ id: "tierAssignedToMember" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
          const tierSetData = tierSetList?.items.find(
            (tierset: any) => tierset.tierSetId === values.tierSetId,
          );

          return (
            <Form onSubmit={handleSubmit}>
              <SelectInput
                className={classes.selectInput}
                adjustListWidthToParent
                data-test-id="assign-tier-set"
                width="100%"
                items={
                  tierSetList?.items.map(({ tierSetId, name }) => ({
                    label: name,
                    id: tierSetId,
                  })) || []
                }
                label={<RequiredFieldLabel text={message({ id: "tierSet" })} />}
                name="tierSetId"
                onChange={() => {
                  setFieldValue("levelId", undefined);
                }}
                data-hj-suppress
              />
              <SelectInput
                className={classes.selectInput}
                displaySearch
                data-test-id="assign-tier"
                width="100%"
                items={
                  tierSetData
                    ? tierSetData.tiers?.map(({ levelId, name }: any) => ({
                        label: name,
                        id: levelId,
                      }))
                    : []
                }
                label={<RequiredFieldLabel text={message({ id: "tier" })} />}
                name="levelId"
                data-hj-suppress
              />
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                size="large"
              >
                {message({ id: "assign" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
